import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Search, Edit, Eye, Copy } from 'lucide-react';
import { Badge } from "../../components/ui/badge";
import Header from '../../components/layout/Header';
import StatusBadge from '../../components/ui/StatusBadge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis
} from '../../components/ui/pagination';
import { useToast } from '../../components/ui/use-toast';
import { collection, doc, getDocs, getFirestore, updateDoc } from "firebase/firestore";
import { app } from "../../firebase";
import { getAuth } from 'firebase/auth';

interface Student {
  id: string;
  studentId: string;
  name: string;
  email: string;
  phone: string;
  uniqueId: string;
  status: 'active' | 'inactive';
}

// Generate 50 mock students
const generateMockStudents = (): any[] => {
  const students: Student[] = [];
  for (let i = 1; i <= 50; i++) {
    const isActive = Math.random() > 0.2; // 80% chance of being active
    students.push({
      id: `${i}`,
      studentId: `ST${(10000 + i).toString().padStart(5, '0')}`,
      name: `Student ${i}`,
      email: `student${i}@example.com`,
      phone: `+91 ${Math.floor(9000000000 + Math.random() * 1000000000)}`,
      uniqueId: Array.from(Array(20), () => Math.floor(Math.random() * 36).toString(36)).join('').toUpperCase(),
      status: isActive ? 'active' : 'inactive'
    });
  }
  return students;
};

const MOCK_STUDENTS = generateMockStudents();

import { collection as fbCollection, getDoc, doc as fbDoc, getFirestore as fbGetFirestore } from "firebase/firestore";

const CheckStudentsData = () => {
  const [userRole, setUserRole] = useState<string>("");
    // Fetch user role
    useEffect(() => {
      const fetchUserRole = async () => {
        try {
          const auth = getAuth(app);
          const db = fbGetFirestore(app);
          const user = auth.currentUser;
          if (user) {
            const userDoc = await getDoc(fbDoc(db, "users", user.uid));
            if (userDoc.exists()) {
              setUserRole(userDoc.data().role || "");
            }
          }
        } catch (e) {
          setUserRole("");
        }
      };
      fetchUserRole();
    }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [students, setStudents] = useState<any[]>(MOCK_STUDENTS);
  const [viewingStudent, setViewingStudent] = useState<any | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const db = getFirestore(app);
  const auth = getAuth(app);


  const studentsPerPage = 15;

  const filteredStudents = students.filter(
    (student) =>
      student?.studentsName?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
      student?.emailId?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
      student?.studentId?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
      student?.mobileNumber.includes(searchQuery) ||
      student?.id?.toLowerCase().includes(searchQuery?.toLowerCase())
  );

  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirstStudent, indexOfLastStudent);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const studentsCollection = collection(db, "students"); // Replace `db` with your Firestore instance  
        const studentSnapshot = await getDocs(studentsCollection);
        let studentList = studentSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        studentList = studentList.filter((x: any) => x.feeTotal > '0').map((data: any) => {
          return {
            ...data,
            feeStatus: data?.feeDue > 0 ? 'pending' : 'paid'
          }
        });
        setStudents(studentList); // Set the fetched students to state 
        // const filtered = JSON.parse(JSON.stringify(getFilteredRecords()));
        // setFilteredRecords(studentList);
      } catch (error) {
        console.error("Error fetching students:", error);
        toast({
          title: "Error",
          description: "Failed to load student records. Please try again.",
          variant: "destructive",
        });
      } finally {
        //setLoading(false); // Stop loading regardless of success or failure  
      }
    };




    fetchStudents();
    //setFilteredRecords(getFilteredRecords());

  }, []);
  const paginate = (pageNumber: number) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleEditStudent = (student: Student) => {
    navigate(`/students/register?id=${student.studentId}`);

    toast({
      title: "Editing Student",
      description: `You are now editing ${student.name}'s information.`,
    });
  };


  const renderStatusBadge = (status: string) => {
    switch (status) {
      case "Active":
        return <Badge className="bg-green-500">{status}</Badge>;
      case "InActive":
        return <Badge className="bg-red-500">{status}</Badge>;
    }
  };

  const handleViewStudent = (student: Student) => {
    setViewingStudent(student);
    setIsViewDialogOpen(true);
  };


  const handleCopy = (input) => {
    navigator.clipboard.writeText(input); // Copy the Student ID to clipboard  
    setCopyStatus(true); // Change status to indicate it's copied  
    setTimeout(() => {
      setCopyStatus(false); // Reset status after 3 seconds  
    }, 3000);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Check Students Data" subtitle="View and manage student records" />

      <div className="flex-1 p-6 animate-fade-in">
        <Card className="shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 sm:mb-0">Students Database</h2>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="search"
                  placeholder="Search by name, ID, or email..."
                  className="pl-8 w-full"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Mobile No</TableHead>
                    <TableHead>Unique ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentStudents.length > 0 ? (
                    currentStudents.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell className="font-medium">{student.studentIdNo}</TableCell>
                        <TableCell>{student.studentsName}</TableCell>
                        <TableCell>{student.emailId}</TableCell>
                        <TableCell>{student.mobileNumber}</TableCell>
                        <TableCell>
                          <span className="font-mono text-xs">{student.id}</span>
                        </TableCell>
                        <TableCell>
                          {renderStatusBadge(student?.status)}
                        </TableCell>
                        <TableCell className="text-right flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleViewStudent(student)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {userRole === "MASTERADMIN" && (
                            <Button size="sm" variant="ghost" onClick={() => handleEditStudent(student)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-10">
                        <div className="flex flex-col items-center justify-center text-gray-500">
                          <Search className="h-10 w-10 mb-2" />
                          <p className="text-lg">No students found</p>
                          <p className="text-sm">Try adjusting your search query</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="p-4 flex items-center justify-between border-t">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium">{indexOfFirstStudent + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(indexOfLastStudent, filteredStudents.length)}
                </span>{" "}
                of <span className="font-medium">{filteredStudents.length}</span> students
              </div>

              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => paginate(currentPage - 1)}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum = currentPage;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <PaginationItem key={i}>
                        <PaginationLink className="cursor-pointer"
                          isActive={pageNum === currentPage}
                          onClick={() => paginate(pageNum)}
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}

                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <>
                      <PaginationItem className="cursor-pointer">
                        <PaginationEllipsis />
                      </PaginationItem>
                      <PaginationItem className="cursor-pointer">
                        <PaginationLink onClick={() => paginate(totalPages)}>
                          {totalPages}
                        </PaginationLink>
                      </PaginationItem>
                    </>
                  )}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => paginate(currentPage + 1)}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </CardContent>
        </Card>

        {/* Student Details Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="sm:max-w-md" style={{ maxHeight: '95vh' }}>
            <DialogHeader>
              <DialogTitle>Student Details</DialogTitle>
              <DialogDescription>
                Detailed information about the student
              </DialogDescription>
            </DialogHeader>

            {viewingStudent && (
              <div className="grid gap-2 py-2">
                <div className="flex items-center justify-center mb-2">
                  <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-2xl font-semibold">
                    {viewingStudent?.studentsName.split(' ').map((n: string) => n[0]).join('')}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Student ID</p>
                      <p className="font-semibold">{viewingStudent.id}</p>
                    </div>
                    <div className="flex justify-end">  {/* Added flex and justify-end */}
                      <div>
                        <p className="text-sm text-gray-500">Status</p>
                        {renderStatusBadge(viewingStudent?.status)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 pt-2">
                  <div>
                    <p className="text-sm text-gray-500">Full Name</p>
                    <p className="font-semibold">{viewingStudent.studentsName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email Address</p>
                    <p className="font-semibold">{viewingStudent.emailId}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone Number</p>
                    <p className="font-semibold">{viewingStudent.mobileNumber}</p>
                  </div>
                  <div className="flex items-center"> {/* Use flex for alignment of ID and icon */}
                    <div>
                      <p className="text-sm text-gray-500">Unique ID</p>
                      <p className="font-mono text-xs font-semibold flex items-center"> {/* Added flex for alignment */}
                        {viewingStudent.id}
                        <Copy
                          onClick={() => handleCopy(viewingStudent.id)}
                          className={`ml-2 cursor-pointer transition-colors duration-300 ${copyStatus ? 'bg-[#0071bd] text-white' : 'bg-transparent text-gray-500 hover:bg-gray-200'
                            } rounded p-1`} // Added background color classes, rounded corners and padding  
                          size={25} // Adjust the icon size as needed  
                        />
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CheckStudentsData;